/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLazyQuery } from '@apollo/client/react';
import type { ApiResponse } from '@pbk-web/data';
import { useCurrentClient } from '@pbk-web/state';
import type { Translations } from '@pbk-web/types';
import { GQLDateIntervalToPlatformType } from '@pbk-web/types';
import { LanguageUtils } from '@pbk-web/utils';
import { graphql } from 'gql.tada';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Access } from '../models/Access';
import { type ActivityPagedFilter, FormReferenceDirection } from '../models/ClientForm';
import type { ClientFormStatus } from '../models/ClientFormStatus';
import { GQLUserToClientFormUserDto } from '../models/ClientFormUser';
import type { DistributionUserGroupResponse } from '../models/Distribution';
import { GQLDistributionMemberStatusToPlatformType, GQLDistributionSchedulingToPlatformType, PeopleType } from '../models/Distribution';
import type { DocumentListItem } from '../models/Document';
import { GQLPeriodicReviewStatusToPlatformStatus, PlatformPeriodicReviewStatusToGQLStatus } from '../models/PeriodicReview';
import {
  GQLDistributionStatusToPlatformStatus,
  GQLFormTypeToPlatformFormType,
  GQLStatusToPlatformStatus,
  GQLVersionStateToPlatformVersionState,
  PlatformClientFormUserRoleStatusToGQLStatus,
  PlatformDistributionStatusToGQLStatus,
  PlatformFormTypeToGQLFormType,
  PlatformRiskToGQLRisk,
  PlatformStatusToGQLStatus,
  PlatformVersionStateToGQLVersionState,
} from '../models/TableView';

export const ClientFormsGraphQLQuery = graphql(`
  query Activities(
    $after: String
    $search: String
    $roles: [ClientFormRole!]
    $users: [UUID!]
    $distributionStatus: [DistributionStatusFilter!]
    $periodicReviewStatuses: [PeriodicReviewStatus!]
    $riskRatings: [RiskRating!]
    $count: Int!
    $dataJobId: UUID
    $sortBy: String
    $includeNonLatest: Boolean
    $containsDirtyTemplateReference: Boolean! = false
    $whereClause: ClientFormFilterInput
    $versionStates: [ClientFormVersionState!]
    $isKnowledgeGraphForm: Boolean
  ) {
    clientForms(
      after: $after
      first: $count
      search: $search
      roles: $roles
      users: $users
      distributionStatuses: $distributionStatus
      periodicReviewStatuses: $periodicReviewStatuses
      riskRatings: $riskRatings
      dataJobId: $dataJobId
      sortBy: $sortBy
      includeNonLatest: $includeNonLatest
      containsDirtyTemplateReference: $containsDirtyTemplateReference
      where: $whereClause
      versionStates: $versionStates
      isKnowledgeGraphForm: $isKnowledgeGraphForm
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        subtitle
        accessType
        archivedUtc
        clientId
        clientModuleId
        clientModuleSectionId
        createdUtc
        customIdentifier
        dueDateUtc
        effectiveDateUtc
        publicCode
        version
        modifiedUtc
        originId
        progress
        riskScore
        status
        statusUtc
        type
        versionState
        lastPeriodicReviewCompletedUtc

        shares {
          sharedClientId
        }

        users {
          userId
          role
          formSectionId
          requiresAction
          sortOrder
          # addedAsIndividual
          pbkUser {
            id
            firstName
            lastName
            userImageId
          }
        }

        periodicReview {
          id
          nextReviewStartDate
          dueDateUtc
          createdUtc
          status
        }

        distribution {
          id
          clientFormId
          clientFormVersion
          createdByName
          createdById
          preferences {
            scheduleType
            scheduleFixedDate
            reminderFrequencyValue
            reminderFrequencyInterval
            notifyOnReady
            notifyOnSuccessful
            linkExpirationAmount
            linkExpirationInterval
            linkExpirationDate
          }
          settings {
            enableAcknowledgementSignature
            enableUncontrolledCopyWatermark
            automaticSendUncontrolledCopy
          }
          status
          startDateUtc
          distributionMembers {
            memberId
            status
            statusUtc
            type
            # addedAsIndividual
            notifiedOnUtc
            reminderSentUtc
            tagIds
          }
        }

        templateFormId
        templateFormVersion
        templateForm {
          isSystem
          requiresApproval
          requiresValidation
          translations
        }

        templateModuleInfo {
          templateModuleId
          templateModuleSectionId
          templateModuleTranslations
        }

        documentInfo {
          documentId
          documentNumber
          prefix
          description
          fileFormat
          fileSize
        }

        references {
          id
        }

        attachments {
          id
        }

        templateReferences {
          ignoreDirty
          latestVersion
          lockedVersion
        }

        trainingRequirementsUnmet
      }
    }
  }
`);

export const useFetchActivitiesGQL = <TTransformed extends object | null = ApiResponse<DocumentListItem[]>>(
  formsFilter: ActivityPagedFilter | undefined,
  currentPage: number,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setData: Dispatch<SetStateAction<TTransformed>>,
  setDataTransformer?: (data: ApiResponse<DocumentListItem[]> | null) => TTransformed,
) => {
  const [gqlCursor, setGqlCursor] = useState<string | null>(null);
  const gqlCursorRef = useRef(gqlCursor);
  gqlCursorRef.current = gqlCursor;
  const [loadGraphqlQuery] = useLazyQuery(ClientFormsGraphQLQuery, {
    fetchPolicy: 'network-only', // disable cache
  });
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();

  const currentClient = useCurrentClient((x) => x.value);

  const gqlFilter = useMemo(
    () => ({
      search: formsFilter?.title,
      roles: formsFilter?.roles?.map((x) => PlatformClientFormUserRoleStatusToGQLStatus[x]),
      users: formsFilter?.users,
      distributionStatus: formsFilter?.distributionStatus?.map((x) => (PlatformDistributionStatusToGQLStatus as any)[x]),
      periodicReviewStatuses: formsFilter?.periodicReviewStatuses?.map((x) => (PlatformPeriodicReviewStatusToGQLStatus as any)[x]),
      riskRatings: formsFilter?.riskStatuses?.map((x) => (PlatformRiskToGQLRisk as any)[x]),
      count: formsFilter?.pageSize ?? 15,
      dataJobId: formsFilter?.importId,
      sortBy: formsFilter?.sortBy,
      containsDirtyTemplateReference: formsFilter?.containsDirtyTemplateReference ?? false,
      includeNonLatest: formsFilter?.includeNonLatest ?? true,
      versionStates: formsFilter?.versionState !== undefined ? [PlatformVersionStateToGQLVersionState[formsFilter.versionState]] : undefined,
      countReferences: formsFilter?.countReferences ?? FormReferenceDirection.Both,
      isKnowledgeGraphForm: formsFilter?.isKnowledgeGraphForm,
      whereClause: {
        id:
          !formsFilter?.includedFormIds && !formsFilter?.excludedFormIds
            ? undefined
            : {
                ...(formsFilter?.includedFormIds ? { in: formsFilter.includedFormIds } : {}),
                ...(formsFilter?.excludedFormIds ? { nin: formsFilter.excludedFormIds } : {}),
              },
        clientId: { eq: currentClient?.id },
        clientModuleId: formsFilter?.clientModuleId ? { eq: formsFilter?.clientModuleId } : undefined,
        archivedUtc: formsFilter?.isArchived === undefined ? undefined : formsFilter?.isArchived ? { neq: null } : { eq: null },
        type: { in: formsFilter?.types?.map((x) => (PlatformFormTypeToGQLFormType as any)[x]) },
        dueDateUtc: formsFilter?.dueDateRange
          ? { lte: formsFilter?.dueDateRange?.end?.toISOString(), gte: formsFilter?.dueDateRange?.start?.toISOString() }
          : undefined,
        status:
          formsFilter?.formStatus === undefined
            ? undefined
            : { in: formsFilter?.formStatus?.map((x) => PlatformStatusToGQLStatus[x as ClientFormStatus]) },
        templateFormId: formsFilter?.templateIds?.length ? { in: formsFilter?.templateIds } : undefined,
      },
    }),
    [
      formsFilter?.title,
      formsFilter?.roles,
      formsFilter?.users,
      formsFilter?.distributionStatus,
      formsFilter?.periodicReviewStatuses,
      formsFilter?.riskStatuses,
      formsFilter?.pageSize,
      formsFilter?.importId,
      formsFilter?.sortBy,
      formsFilter?.containsDirtyTemplateReference,
      formsFilter?.includeNonLatest,
      formsFilter?.versionState,
      formsFilter?.countReferences,
      formsFilter?.isKnowledgeGraphForm,
      formsFilter?.includedFormIds,
      formsFilter?.excludedFormIds,
      formsFilter?.clientModuleId,
      formsFilter?.isArchived,
      formsFilter?.types,
      formsFilter?.dueDateRange,
      formsFilter?.formStatus,
      formsFilter?.templateIds,
      currentClient?.id,
    ],
  );

  const loadGraphql = useCallback(
    async (after: string | null) => {
      setLoading(true);
      const res = await loadGraphqlQuery({
        variables: { ...gqlFilter, after },
      });

      const newCursor = res.data?.clientForms?.pageInfo.endCursor ?? null;
      setGqlCursor(newCursor);

      const totalCount = res.data?.clientForms?.totalCount ?? 0;
      const nodes = res.data?.clientForms?.nodes ?? [];

      const transformedActivities = nodes
        .map(
          (x) =>
            x && {
              ...x.documentInfo,
              accessType: x.accessType === 'INTRA_CLIENT' ? Access.intraClient : Access.restricted,
              archivedUtc: x.archivedUtc,
              clientId: x.clientId,
              clientModuleId: x.clientModuleId,
              clientModuleSectionId: x.clientModuleSectionId,
              createdUtc: x.createdUtc,
              customIdentifier: x.customIdentifier,
              dueDateUtc: x.dueDateUtc,
              effectiveDateUtc: x.effectiveDateUtc,
              id: x.id,
              isPublic: !!x.publicCode,
              isShared: (x.shares ?? []).length > 0,
              shares: x.shares,
              isSystemTemplateForm: x.templateForm!.isSystem,
              majorVersion: x.version,
              modifiedUtc: x.modifiedUtc,
              originId: x.originId,
              progress: x.progress,
              requiresApproval: x.templateForm!.requiresApproval,
              requiresValidation: x.templateForm!.requiresValidation,
              riskScore: x.riskScore === 999_999_999 ? Number('Infinity') : x.riskScore,
              status: GQLStatusToPlatformStatus[x.status],
              statusUtc: x.statusUtc,
              versionState: x.versionState ? GQLVersionStateToPlatformVersionState[x.versionState as string] : undefined,
              subtitle: x.subtitle,
              templateId: x.templateFormId,
              templateModuleName: LanguageUtils.getTranslation(
                'name',
                x.templateModuleInfo?.templateModuleTranslations as Translations,
                currentLanguage,
              ),
              templateModuleSectionId: x.templateModuleInfo?.templateModuleSectionId,
              templateModuleTranslations: x.templateModuleInfo?.templateModuleTranslations as Translations,
              totalRiskScore: x.riskScore,
              translations: x.templateForm!.translations,
              type: GQLFormTypeToPlatformFormType[x.type],
              users: GQLUserToClientFormUserDto(x.users),
              distribution: !x.distribution
                ? null
                : {
                    id: x.distribution.id,
                    clientFormId: x.distribution.clientFormId,
                    clientFormVersion: x.distribution.clientFormVersion,
                    createdBy: x.distribution.createdByName,
                    createdById: x.distribution.createdById,
                    members: x.distribution.distributionMembers.map((member) => ({
                      memberId: member.memberId,
                      distributionId: x.distribution!.id,
                      status: GQLDistributionMemberStatusToPlatformType[member.status],
                      statusUtc: member.statusUtc,
                      type: member.type === 'USER' ? PeopleType.Member : PeopleType.Contact,
                      addedAsIndividual: false, //member.addedAsIndividual,
                      notifiedOnUtc: member.notifiedOnUtc,
                      reminderSentUtc: member.reminderSentUtc,
                      tagIds: member.tagIds,
                    })),
                    preferences: {
                      ...x.distribution.preferences,
                      scheduleType: GQLDistributionSchedulingToPlatformType[x.distribution.preferences.scheduleType],
                      reminderFrequencyInterval:
                        x.distribution.preferences.reminderFrequencyInterval === null
                          ? null
                          : GQLDateIntervalToPlatformType[x.distribution.preferences.reminderFrequencyInterval],
                      linkExpirationInterval:
                        x.distribution.preferences.linkExpirationInterval === null
                          ? null
                          : GQLDateIntervalToPlatformType[x.distribution.preferences.linkExpirationInterval],
                    },
                    scheduleType: GQLDistributionSchedulingToPlatformType[x.distribution.preferences.scheduleType],
                    status: GQLDistributionStatusToPlatformStatus[x.distribution.status],
                    settings: x.distribution.settings,
                    tags: [] as DistributionUserGroupResponse[],
                    startDate: x.distribution.startDateUtc,
                  },
              lastReviewCompletedDateUtc: x.lastPeriodicReviewCompletedUtc,
              nextReviewStartDateUtc: x.periodicReview?.nextReviewStartDate,
              periodicReviewCreatedDateUtc: x.periodicReview?.createdUtc,
              periodicReviewDueDate: x.periodicReview?.dueDateUtc,
              periodicReviewDueDateUtc: x.periodicReview?.dueDateUtc,
              periodicReviewStatus: x.periodicReview ? GQLPeriodicReviewStatusToPlatformStatus[x.periodicReview.status] : null,
              referenceCount: x.references?.length ?? 0,
              attachmentCount: x.attachments?.length ?? 0,
              templateReferenceCount: x.templateReferences?.length ?? 0,
              templateReferences: x.templateReferences,
              trainingRequirementsUnmet: x.trainingRequirementsUnmet,
            },
        )
        .filter(Boolean);

      const dataTransformer = setDataTransformer ?? ((data: ApiResponse<DocumentListItem[]>) => data as TTransformed);

      setData((prev) => {
        const prevData = prev && (('data' in prev && 'meta' in prev ? prev.data : prev) as any[]);

        return dataTransformer({
          data: (after ? [...(prevData || []), ...transformedActivities] : transformedActivities) as DocumentListItem[],
          meta: {
            success: true,
            code: 200,
            message: '',
          },
          hasNextPage: res.data?.clientForms?.pageInfo.hasNextPage ?? false,
          totalCount,
          hasPreviousPage: currentPage > 1,
          pageNumber: currentPage,
          totalPages: Math.ceil(totalCount / (formsFilter?.pageSize || 15)),
          status: 200,
          headers: {},
        });
      });

      setLoading(false);
    },
    [currentLanguage, currentPage, formsFilter?.pageSize, gqlFilter, loadGraphqlQuery, setData, setDataTransformer, setLoading],
  );

  return [loadGraphql, gqlCursorRef] as const;
};
